import type { ResponseOf, RouteDef } from '@pmai/contracts';
import { useToast } from '@pmai/ui';
import {
  type QueryKey,
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { type ApiError, call, type CallOptions } from './client';

/**
 * TanStack Query bindings for the route catalogue — every page loads and changes data through
 * these two hooks. Query keys start with the route path, so a mutation invalidates by route.
 */

export const queryKeyFor = (route: RouteDef, opts: CallOptions<RouteDef> = {}): QueryKey => [
  route.path,
  opts.params ?? {},
  opts.query ?? {},
];

export function useApiQuery<R extends RouteDef>(
  route: R,
  opts: CallOptions<R> = {},
  options: Omit<UseQueryOptions<ResponseOf<R>, ApiError>, 'queryKey' | 'queryFn'> = {},
) {
  return useQuery<ResponseOf<R>, ApiError>({
    queryKey: queryKeyFor(route, opts as CallOptions<RouteDef>),
    queryFn: () => call(route, opts),
    ...options,
  });
}

export interface ApiMutationConfig<R extends RouteDef> extends Omit<
  UseMutationOptions<ResponseOf<R>, ApiError, CallOptions<R>>,
  'mutationFn'
> {
  /** Routes whose cached data this change makes stale (e.g. the list after a create). */
  invalidates?: readonly RouteDef[];
  /** Translation key of the confirmation toast shown after success ("Project created"). */
  success?: string;
}

export function useApiMutation<R extends RouteDef>(route: R, config: ApiMutationConfig<R> = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const notify = useToast();
  const { invalidates = [], success, onSuccess, ...rest } = config;
  return useMutation<ResponseOf<R>, ApiError, CallOptions<R>>({
    mutationFn: (opts) => call(route, opts),
    ...rest,
    onSuccess: async (...args) => {
      await Promise.all(invalidates.map((r) => queryClient.invalidateQueries({ queryKey: [r.path] })));
      if (success) notify(t(success));
      await onSuccess?.(...args);
    },
  });
}
